import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { orEmptyIfForbidden, createMaterialUsage, getInventory, getMaterialUsages, updateMaterialUsage } from '../../services/api';
import { calculateSubtotal, can, formatMoney, formatQuantity } from '../../lib/inventory';

const EMPTY_FORM = { inventory_item_id: '', quantity: '', allow_negative: false, justification: '' };

const MaterialUsagePanel = ({ parentType, parentId, user, initialUsages, initialInventory }) => {
  const [usages, setUsages] = useState(initialUsages || []);
  const [inventory, setInventory] = useState(initialInventory || []);
  const [loading, setLoading] = useState(initialUsages === undefined);
  const [addOpen, setAddOpen] = useState(false);
  const [correction, setCorrection] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [correctionQuantity, setCorrectionQuantity] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [saving, setSaving] = useState(false);
  const parentParam = parentType === 'project' ? 'project_id' : 'service_request_id';

  const load = useCallback(async () => {
    if (!parentId) return;
    setLoading(true);
    try {
      const [usageResponse, inventoryResponse] = await Promise.all([
        getMaterialUsages({ [parentParam]: parentId }),
        orEmptyIfForbidden(getInventory()),
      ]);
      setUsages(usageResponse.data || []);
      setInventory((inventoryResponse.data || []).filter((item) => item.active !== false));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger le matériel');
    } finally {
      setLoading(false);
    }
  }, [parentId, parentParam]);

  useEffect(() => {
    if (initialUsages === undefined || initialInventory === undefined) load();
  }, [initialInventory, initialUsages, load]);

  const selectedItem = useMemo(
    () => inventory.find((item) => item.id === form.inventory_item_id),
    [form.inventory_item_id, inventory]
  );
  const subtotal = useMemo(() => {
    if (!selectedItem || !form.quantity) return null;
    try { return calculateSubtotal(form.quantity, selectedItem.sale_price || '0'); }
    catch { return null; }
  }, [form.quantity, selectedItem]);

  const canCorrect = (usage) => usage.billing_status !== 'billed' && (
    can(user, 'inventory.usage.correct_any') ||
    (can(user, 'inventory.usage.correct_own') && usage.technician_id === user?.id)
  );

  const submitUsage = async (event) => {
    event.preventDefault();
    if (form.allow_negative && !form.justification.trim()) {
      toast.error('La justification est obligatoire pour un stock négatif');
      return;
    }
    setSaving(true);
    try {
      await createMaterialUsage({
        inventory_item_id: form.inventory_item_id,
        quantity: form.quantity,
        [parentParam]: parentId,
        allow_negative: form.allow_negative,
        justification: form.allow_negative ? form.justification.trim() : null,
      });
      toast.success('Matériel ajouté');
      setForm(EMPTY_FORM);
      setAddOpen(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible d’ajouter le matériel');
    } finally { setSaving(false); }
  };

  const submitCorrection = async (event) => {
    event.preventDefault();
    if (!correctionReason.trim()) return toast.error('Le motif de correction est obligatoire');
    setSaving(true);
    try {
      await updateMaterialUsage(correction.id, {
        quantity: correctionQuantity,
        reason: correctionReason.trim(),
      });
      toast.success('Consommation corrigée');
      setCorrection(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de corriger la consommation');
    } finally { setSaving(false); }
  };

  return (
    <section className="border-t pt-5 mt-5" aria-label="Matériel utilisé">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-slate-900">Matériel utilisé</h3>
          <p className="text-sm text-slate-500">Les prix sont figés au moment de l’utilisation.</p>
        </div>
        {can(user, 'inventory.usage.create') && (
          <Button type="button" size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />Ajouter du matériel
          </Button>
        )}
      </div>

      {loading ? <p className="text-sm text-slate-500">Chargement…</p> : usages.length === 0 ? (
        <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-4">Aucun matériel enregistré.</p>
      ) : (
        <div className="space-y-3">
          {usages.map((usage) => (
            <div key={usage.id} className="rounded-lg border bg-white p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-900">{usage.item_name}</p>
                  <p className="text-sm text-slate-600">
                    {formatQuantity(usage.quantity)} {usage.unit || 'unité'} × {formatMoney(usage.unit_sale_price)}
                    {' = '}<span className="font-medium">{formatMoney(usage.subtotal)}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {usage.technician_name || 'Technicien'}
                    {usage.created_at ? ' · ' + new Date(usage.created_at).toLocaleDateString('fr-CA') : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={usage.billing_status === 'billed'
                    ? 'text-xs rounded-full bg-emerald-100 text-emerald-800 px-2 py-1'
                    : 'text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-1'}>
                    {usage.billing_status === 'billed' ? 'Facturé' : 'À facturer'}
                  </span>
                  {canCorrect(usage) && (
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      setCorrection(usage);
                      setCorrectionQuantity(String(usage.quantity));
                      setCorrectionReason('');
                    }}>
                      <Pencil className="w-4 h-4 mr-1" />Corriger
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Ajouter du matériel</DialogTitle></DialogHeader>
          <form onSubmit={submitUsage} className="space-y-4">
            <div>
              <Label htmlFor="usage-item">Article</Label>
              <select id="usage-item" className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={form.inventory_item_id}
                onChange={(event) => setForm({ ...form, inventory_item_id: event.target.value })} required>
                <option value="">Sélectionner un article</option>
                {inventory.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} — stock {formatQuantity(item.quantity || '0')} {item.unit || ''}
                  </option>
                ))}
              </select>
            </div>
            {selectedItem && (
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <p>Disponible : <strong>{formatQuantity(selectedItem.quantity || '0')} {selectedItem.unit || ''}</strong></p>
                <p>Prix de vente : <strong>{formatMoney(selectedItem.sale_price || '0')}</strong></p>
              </div>
            )}
            <div>
              <Label htmlFor="usage-quantity">Quantité utilisée</Label>
              <Input id="usage-quantity" type="number" min="0.001" step="any" value={form.quantity}
                onChange={(event) => setForm({ ...form, quantity: event.target.value })} required />
            </div>
            {subtotal && <p className="text-sm">Sous-total à facturer : <strong>{formatMoney(subtotal)}</strong></p>}
            {can(user, 'inventory.override_negative') && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.allow_negative}
                    onChange={(event) => setForm({ ...form, allow_negative: event.target.checked })} />
                  Autoriser exceptionnellement un stock négatif
                </label>
                {form.allow_negative && (
                  <div>
                    <Label htmlFor="negative-justification">Justification obligatoire</Label>
                    <Input id="negative-justification" value={form.justification}
                      onChange={(event) => setForm({ ...form, justification: event.target.value })} required />
                  </div>
                )}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer l’utilisation'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(correction)} onOpenChange={(open) => !open && setCorrection(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Corriger la consommation</DialogTitle></DialogHeader>
          <form onSubmit={submitCorrection} className="space-y-4">
            <div>
              <Label htmlFor="correction-quantity">Quantité correcte</Label>
              <Input id="correction-quantity" type="number" min="0" step="any" value={correctionQuantity}
                onChange={(event) => setCorrectionQuantity(event.target.value)} required />
            </div>
            <div>
              <Label htmlFor="correction-reason">Motif de la correction</Label>
              <Input id="correction-reason" value={correctionReason}
                onChange={(event) => setCorrectionReason(event.target.value)} required />
            </div>
            <Button type="submit" className="w-full" disabled={saving}>
              {saving ? 'Correction…' : 'Confirmer la correction'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default MaterialUsagePanel;
